import BackButton from "../components/BackButton";
import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useApp } from "../lib/AppContext";
import { Loader2 } from "lucide-react";

export default function Register() {
  const { config, user } = useApp();
  const nav = useNavigate();
  const [ownerExists, setOwnerExists] = useState(null);
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [email, setEmail] = useState("");
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [privacy, setPrivacy] = useState(false);
  const [terms, setTerms] = useState(false);
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState("form"); // form | otp
  const [otp, setOtp] = useState("");

  useEffect(() => {
    if (user) nav("/", { replace: true });
  }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.register({
        first_name: first.trim(), last_name: last.trim(), email: email.trim(),
        password: pwd, password_confirm: pwd2,
        privacy_accepted: privacy, terms_accepted: terms,
      });
      toast.success("Registrazione avviata. Controlla la tua email per il codice.");
      setStep("otp");
    } catch (err) {
      if (err?.response?.status === 409) setOwnerExists(true);
      toast.error(err?.response?.data?.detail || "Impossibile registrarsi");
    } finally { setBusy(false); }
  };

  const verify = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.verifyEmail(email.trim(), otp);
      toast.success("Email verificata. Ora accedi.");
      nav("/login", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Codice non valido o scaduto");
    } finally { setBusy(false); }
  };

  const pwdOk = pwd.length >= 12 && /[A-Za-z]/.test(pwd) && /\d/.test(pwd);
  const strength = Math.min(4, (pwd.length >= 12 ? 1 : 0) + (pwd.length >= 16 ? 1 : 0) + (/[A-Z]/.test(pwd) && /[a-z]/.test(pwd) ? 1 : 0) + (/\d/.test(pwd) ? 1 : 0) + (/[^A-Za-z0-9]/.test(pwd) ? 1 : 0));

  return (
    <div className="min-h-dvh pxos-gradient-surface grid place-items-center px-4" data-testid="register-page">
      <BackButton to="/" testid="register-back-btn" />
      <div className="w-full max-w-md pxos-card p-8">
        <h1 className="font-display text-2xl font-semibold text-center">Registra l'owner</h1>
        <p className="mt-1 text-sm opacity-70 text-center">Solo il primo owner può registrarsi qui. I collaboratori arrivano via invito.</p>

        {ownerExists && (
          <div className="mt-4 text-sm p-3 rounded-xl" style={{ background: "var(--pxos-primary-lightest, #F0F6FF)" }}>
            Un owner è già registrato. <Link to="/login" className="underline">Vai al login</Link>.
          </div>
        )}

        {step === "form" ? (
          <form onSubmit={submit} className="mt-6 space-y-3" data-testid="register-form">
            <div className="grid grid-cols-2 gap-3">
              <label className="block"><span className="text-xs font-semibold uppercase tracking-widest">Nome</span>
                <input required value={first} onChange={(e) => setFirst(e.target.value)} className="pxos-input" data-testid="register-first" /></label>
              <label className="block"><span className="text-xs font-semibold uppercase tracking-widest">Cognome</span>
                <input required value={last} onChange={(e) => setLast(e.target.value)} className="pxos-input" data-testid="register-last" /></label>
            </div>
            <label className="block"><span className="text-xs font-semibold uppercase tracking-widest">Email</span>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="pxos-input" data-testid="register-email" /></label>
            <label className="block"><span className="text-xs font-semibold uppercase tracking-widest">Password (min 12)</span>
              <input type="password" required minLength={12} value={pwd} onChange={(e) => setPwd(e.target.value)} className="pxos-input" data-testid="register-pwd" /></label>
            <div className="h-1.5 rounded-full bg-gray-200 overflow-hidden" data-testid="register-strength">
              <div className="h-full transition-all" style={{ width: `${(strength/4)*100}%`, background: strength < 2 ? "#e88a94" : strength < 3 ? "#e0b96a" : "#7fbb87" }} />
            </div>
            <label className="block"><span className="text-xs font-semibold uppercase tracking-widest">Conferma password</span>
              <input type="password" required minLength={12} value={pwd2} onChange={(e) => setPwd2(e.target.value)} className="pxos-input" data-testid="register-pwd2" /></label>
            <label className="flex items-center gap-2 text-xs">
              <input type="checkbox" required checked={privacy} onChange={(e) => setPrivacy(e.target.checked)} data-testid="register-privacy" />
              Accetto la <Link to="/privacy" className="underline">privacy</Link>
            </label>
            <label className="flex items-center gap-2 text-xs">
              <input type="checkbox" required checked={terms} onChange={(e) => setTerms(e.target.checked)} data-testid="register-terms" />
              Accetto i <Link to="/terms" className="underline">termini</Link>
            </label>
            <button disabled={busy || !pwdOk} className="pxos-btn-primary w-full" data-testid="register-submit">
              {busy ? <Loader2 className="inline animate-spin" size={16}/> : "Registrati"}
            </button>
            <p className="text-xs text-center opacity-70">Hai già un account? <Link to="/login" className="underline">Accedi</Link></p>
          </form>
        ) : (
          <form onSubmit={verify} className="mt-6 space-y-3" data-testid="register-otp-form">
            <div className="text-sm">Codice OTP inviato a <strong>{email}</strong>. Valido 10 minuti.</div>
            <label className="block"><span className="text-xs font-semibold uppercase tracking-widest">Codice ricevuto</span>
              <input value={otp} onChange={(e) => setOtp(e.target.value)} className="pxos-input" placeholder="123456" data-testid="register-otp-code" /></label>
            <button disabled={busy || otp.length !== 6} className="pxos-btn-primary w-full" data-testid="register-otp-submit">
              {busy ? <Loader2 className="inline animate-spin" size={16}/> : "Verifica email"}
            </button>
            <button type="button" onClick={() => api.resendOtp(email, "register_verify").then(() => toast("Nuovo codice inviato."))}
                    className="text-xs opacity-70 underline w-full text-center" data-testid="register-otp-resend">
              Rinvia codice
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
